import { Report } from '../../reports/action';
import { Goal, GoalEvolutionCalculus } from '../../reports/generic/action';
import type { Report as BareReport, Panel, Row } from '../../reports/interpretor/bareReportingDecoder';
import { goalPathHash } from '../../reports/interpretor/ReportInterpretor';
import type { DashboardChartConfigWithChartData } from '../action';
import { DashboardState, InnerValue, innerValueToNumber, valueToString } from './dashboard_script';

type GoalNode = { goal: number | undefined, valueN?: InnerValue, valueN1?: InnerValue, evolution?: InnerValue, primaryValue: InnerValue };

export type GoalTree = Map<string, GoalNode & { children: GoalTree | undefined }>;


async function getGoals(foundationUrl: string, bearerToken: string, report_id: number): Promise<Goal[]> {
	return await fetch(`${foundationUrl}/api/v2/goals/${report_id}`, {
		method: 'GET',
		headers: {
			'Authorization': `Bearer ${bearerToken}`,
			'Content-Type': 'application/json'
		}
	})
		.then(response => response.json());
}

export async function getGoalsAndGenerateTree(chart: DashboardChartConfigWithChartData, definition: Report, report: BareReport, state: DashboardState): Promise<GoalTree | undefined> {
	if (!chart.data.goals || chart.data.values_index === undefined || chart.report_id == null) return;
	const columnName = report.panels.length === 1 ? '' : valueToString(report.panels[chart.data.panel_index]?.value, state);
	const date = valueToString(report.panels[chart.data.panel_index]?.headers.filter(h => !h.primary)[chart.data.values_index!]?.cell.value, state);
	const goals = await getGoals(state.foundationUrl, state.authToken, chart.report_id);
	const nbPrimanyColumns = report.panels[chart.data.panel_index].headers.reduce((acc, curr) => acc + (curr.primary ? 1 : 0), 0);
	const goalTree = createGoalsTree(goals, columnName, date, nbPrimanyColumns);
	enrichTreeWithPanel(goalTree, report.panels[chart.data.panel_index], chart.data, nbPrimanyColumns, definition);
	return goalTree;
}

function createGoalsTree(goals: Goal[], columnName: string | undefined, date: string | undefined, nbPrimaryColumns: number): GoalTree {
	const tree: GoalTree = new Map();
	if (columnName === undefined || date === undefined) return tree;

	for (const goal of goals) {
		if (goal.column_name === columnName && goal.goals.some(g => g.date === date)) {
			iterateGoal(goal, tree, date, 0, nbPrimaryColumns - 1);
		}
	}
	return tree;
}

function iterateGoal(goal: Goal, tree: GoalTree, date: string, depth: number, max: number) {
	const pathValue = Object.values(goal.path).find(path => typeof (path) !== 'string' && path.index === depth);
	if (typeof (pathValue) === 'string' || !pathValue) return;
	const pathString = goalPathHash(pathValue.value);
	if (depth === max) {
		const value = goal.goals.find(g => g.date === date)?.value;
		if (value != null) tree.set(pathString, { goal: value, children: undefined, primaryValue: pathValue.value });
	} else {
		const node = tree.get(pathString);
		if (!node) {
			const newNode = { children: new Map(), goal: undefined, primaryValue: pathValue.value };
			iterateGoal(goal, newNode.children!, date, depth + 1, max);
			tree.set(pathString, newNode);
		} else {
			iterateGoal(goal, node.children!, date, depth + 1, max);
		}
	}
}

function enrichTreeWithPanel(tree: GoalTree, panel: Panel, chart: DashboardChartConfigWithChartData['data'], nbPrimaryColumns: number, reportDefinition: Report) {
	if (chart.values_index === undefined) return;
	for (const row of panel.rows) {
		enrichTreeWithPanelRec(tree, row, chart.values_index!, 0, nbPrimaryColumns - 1, reportDefinition);
	}
}

type DenomAndNumer = { denom: number, numer: number };

function innerValueToPercentage(value: InnerValue | undefined): DenomAndNumer {
	if (!value) return { denom: 0, numer: 0 };
	if ('percentage' in value) return value.percentage;
	if ('average' in value) return { denom: value.average.denominator, numer: value.average.numerator };
	if ('float' in value) return { denom: 0, numer: value.float };
	if ('int' in value) return { denom: 0, numer: value.int };
	return { denom: 0, numer: 0 };
}

function addDenomAndNumer(a: DenomAndNumer | undefined, b: DenomAndNumer | undefined): DenomAndNumer | undefined {
	if (!a) return b;
	if (!b) return a;
	return { denom: a.denom + b.denom, numer: a.numer + b.numer };
}


/// To work well:
/// goals MUST be .toFixed(2) and cellValue / cellValueN1 MUST NOT be toFixed(2)
function calcGoalEvolution(cellValue: number, cellValueN1: number | undefined | null, goal: number, reportDefinition: Report): { percentage: { numer: number, denom: number } } {
	if (reportDefinition.goals_options?.evolution_calculus !== GoalEvolutionCalculus.Nmin1) {
		return { percentage: { numer: cellValue, denom: goal } };
	}
	let denominator = 1.0;
	let numerator = cellValue;
	if (cellValueN1 != null && cellValueN1 != 0) {
		numerator = cellValue - cellValueN1;
		if (numerator < 0) {
			numerator = 0;
		}
		if (goal - cellValueN1 != 0) {
			const resDenominator = goal - cellValueN1;
			if (resDenominator > 0) denominator = resDenominator;
		}
	} else {
		denominator = goal;
		numerator = cellValue;
	}
	if (numerator / denominator > 1 ||
		(cellValue == cellValueN1 && cellValueN1 >= goal)) {
		return { percentage: { numer: 1, denom: 1 } };
	}
	return { percentage: { numer: numerator, denom: denominator } };
}

function enrichTreeWithPanelRec(tree: GoalTree, row: Row, valueIndex: number, depth: number, maxDepth: number, reportDefinition: Report): GoalNode[] {
	const pathString = goalPathHash(row.primaryCell[depth].value.val);
	const node = tree.get(pathString);
	if (!node) return [];
	if (depth === maxDepth) {
		node.valueN = row.cells[valueIndex].value.val;
		if (valueIndex > 0) {
			node.valueN1 = row.cells[valueIndex - 1].value.val;
		}
		const valueN = innerValueToNumber(node.valueN);
		const valueN1 = node.valueN1 ? innerValueToNumber(node.valueN1) : undefined;
		if (valueN !== null && node.goal != null) {
			node.evolution = calcGoalEvolution(valueN, valueN1, node.goal, reportDefinition);
		}
		return [node];
	} else {
		const childrenGoals: GoalNode[] = [];
		for (const subRow of row.rows) {
			childrenGoals.push(...enrichTreeWithPanelRec(node.children!, subRow, valueIndex, depth + 1, maxDepth, reportDefinition));
		}
		const { n, n1, goal, firstNonNullChild } = childrenGoals.reduce((acc: { n: DenomAndNumer | undefined, n1: DenomAndNumer | undefined, goal: number, firstNonNullChild: InnerValue | undefined }, node) => {
			acc.n = addDenomAndNumer(acc.n, innerValueToPercentage(node.valueN));
			acc.n1 = addDenomAndNumer(acc.n1, innerValueToPercentage(node.valueN1));
			if (node.goal !== undefined) acc.goal += node.goal;
			if (!acc.firstNonNullChild && node.valueN && (
				'percentage' in node.valueN ||
				'average' in node.valueN ||
				'float' in node.valueN ||
				'int' in node.valueN)) {
				acc.firstNonNullChild = node.valueN;
			}
			return acc;
		}, { n: undefined, n1: undefined, goal: 0, firstNonNullChild: undefined });
		if (firstNonNullChild && ('percentage' in firstNonNullChild || 'average' in firstNonNullChild)) {
			node.goal = parseFloat((goal / childrenGoals.length).toFixed(2));
		} else {
			node.goal = goal;
		}

		// expect all children to have the same type of InnerValue, if not, fuck
		let cellValue: number | null = null;
		let cellValueN1: number | null = null;
		if (firstNonNullChild) {
			if ('percentage' in firstNonNullChild) {
				cellValue = n ? innerValueToNumber({ percentage: n }) : null;
				cellValueN1 = n1 ? innerValueToNumber({ percentage: n1 }) : null;
			} else if ('average' in firstNonNullChild) {
				cellValue = n ? innerValueToNumber({ average: { denominator: n.denom, numerator: n.numer } }) : null;
				cellValueN1 = n1 ? innerValueToNumber({ average: { denominator: n1.denom, numerator: n1.numer } }) : null;
			} else if ('float' in firstNonNullChild || 'int' in firstNonNullChild) {
				cellValue = n ? innerValueToNumber({ float: n.numer }) : null;
				cellValueN1 = n1 ? innerValueToNumber({ float: n1.numer }) : null;
			}
		}

		if (cellValue !== null) {
			node.evolution = calcGoalEvolution(cellValue, cellValueN1, node.goal, reportDefinition);
		}

		return childrenGoals;
	}
}
